import React, { useEffect, useState } from "react";
import { Calendar, Users, Star, Plus, MessageCircle, Heart, User, MapPin,
  BookOpen, BarChart3, Settings, Moon, Sun, Instagram, Facebook, Youtube,
  Phone, IndianRupee,Shield,
  Upload,
  Loader2,X } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { deleteClass, fetchClasses, fetchprofiledata, fetchTodaysSchedule, formatProfileLocation } from "./profileAPIs";
import AddClassForm from "./addNewClass";
import ClassCard from "./ClassCard";
import toast from "react-hot-toast";
import { Helmet } from "react-helmet";
import ComingSoon from "./ComingSoon";
import { motion, AnimatePresence } from "framer-motion";
import InactiveClasses from "./inactiveClasses";
import { adminApi } from "../../config";
import { toIsoDate } from "../../utils/date";
import SubRoleSelector from "../common/SubRoleSelector";

interface DeleteConfirmationModalProps {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  className?: string;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  show,
  onClose,
  onConfirm,
  className,
}) => {
  return (
    <AnimatePresence>
      {show && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${className}`}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-lg p-8 shadow-2xl max-w-sm w-full"
          >
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Are you sure?
            </h2>
            <p className="text-gray-600 mb-6">
              Do you really want to delete this class? This process cannot be
              undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

interface userProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  bio?: string;
  location?: string;
  certification?: string;
  languages?: string[];
  skills?: string[];
  profileIcon?: string;
  experience?: string;
  socialLinks: {
    facebook: string | null;
    instagram: string | null;
    telegram: string | null;
    youtube: string | null;
    demoVideo: string | null;
    whatsapp: string | null;
  };
  role: string;
  subRole?: string;
  totalEarnings: number;
  mentorId?: string;
}

interface profiledatatype {
  warning: string;
  profile?: userProfileData;
}

// Backend booking data structure
export interface BookingData {
  _id: string;
  age: string;
  bookingDate: string;
  bookingId: string;
  classCategory: string;
  classEndDate: string;
  classId: string;
  classStartDate: string;
  classTitle: string;
  classesAttended: number;
  classname: string;
  instructorId: string;
  phone: string;
  preferredTimeSlot: string;
  remainingClasses: number;
  specialRequirements: string;
  userId: string;
  pricingOption?: string;
  selectedDates?: string[];
  studentName?: string;
  name?: string;
  isDemo?: boolean;
  demoCompleted?: boolean;
  contact?: string;
  email?: string;
  totalAmount?: number;
  // NEW FIELDS FOR BOOKING TYPE HANDLING
  bookingType?: 'session' | 'demo' | 'monthly';
  demoDates?: string[];              // Specific dates for demo bookings
  demoStartDate?: string;            // Demo period start date
  demoEndDate?: string;              // Demo period end date
  monthlyStartDate?: string;         // Monthly subscription start
  monthlyEndDate?: string;           // Monthly subscription end
}

// Backend class data structure
export interface BackendClassData {
  _id: string;
  SerialCode: string;
  bookingCount: number;
  bookings: BookingData[];
  classCategory: string;
  classDescription: string;
  classDuration: string;
  classEndDate: string;
  classPrice: { session: string; monthly: string; yearly: string };
  classStartDate: string;
  classThumbnail: string;
  classTitle: string;
  createAt: string;
  googleMeetLinks: Array<{
    id: string;
    url: string;
    title: string;
    time: string;
  }>;
  purchaseStatus: string;
  pricingType?: string;
  demoDuration?: string;
  schedule?: {
    [key: string]: { isActive: boolean };
  };
  updateAt: string;
}

export interface BackendResponse {
  data: {
    bookings: BookingData[];
    classes: BackendClassData[];
  };
}

// Define the mapped class interface
export interface MappedClass {
  id: string;
  title: string;
  type: string;
  description: string;
  duration: string;
  enrolled: number;
  capacity: number;
  startDate: string;
  endDate: string;
  schedule: string;
  price: number;
  meetLink: string;
  status: string;
  level: string;
  googleMeetLinks: Array<{
    id: string;
    url: string;
    title: string;
    time: string;
  }>;
  bookings: BookingData[];
  bookingCount: number;
  nextUpcomingSession?: {
    time: string;
    title: string;
    url: string;
    id: string;
  };
  classDurationMinutes: number; // Add this for duration calculation
  pricingType?: string;
  demoDuration?: string;
  scheduleData?: {
    [key: string]: { isActive: boolean };
  };
  classThumbnail?: string; // Add classThumbnail
  classPrice: { session: string; monthly: string; yearly: string }; // Add classPrice
}

interface deleteApiRes {
  success: boolean;
}

export default function InstructorProfilePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") || "classes";
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [showAddClass, setShowAddClass] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [classToDelete, setClassToDelete] = useState<string | null>(null);
  const [showMandatoryPopup, setShowMandatoryPopup] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [submittingDetails, setSubmittingDetails] = useState(false);  
  const [profileData, setProfileData] = useState<userProfileData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    bio: "",
    location: "",
    certification: "",
    languages: [],
    skills: [],
    profileIcon: "",
    socialLinks: {
      facebook: null,
      instagram: null,
      telegram: null,
      youtube: null,
      demoVideo: null,
      whatsapp: null,
    },
    role: "",
    subRole: "",
    totalEarnings: 0,
  });
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  const [classes, setClasses] = useState<MappedClass[]>([]);
  const [allBookings, setAllBookings] = useState<BookingData[]>([]);
  const [todaysScheduleData, setTodaysScheduleData] = useState<any>(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
    const [certificateUrl, setCertificateUrl] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Reset pagination when tab changes and update URL
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearchParams({ tab });
  };

  // Sync URL changes with activeTab state
  useEffect(() => {
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
      setCurrentPage(1);
    }
  }, [tabFromUrl, activeTab]);

  const handleViewCertificate = (url: string) => {
    setCertificateUrl(url);
    setShowCertificateModal(true);
  };

  const handleFetchProfileData = async () => {
    try {
        const data = (await fetchprofiledata()) as any;
        console.log("Full response data:", data.profile);
        if (data?.profile) {
            setProfileData({
              ...data.profile,
              location: formatProfileLocation(data.profile.location),
            });
            
            // CHECK: Agar certification ya bank details nahi hai to popup open karo
            const profile = data.profile;
            
            console.log("data ke andr ye aara hai", profile);
            console.log("certificate", profile.certification);
            console.log("accountNumber", profile.accountNumber);
            
            if (!profile.certification || profile.certification.length === 0 || !profile.bankName ) {
                setShowMandatoryPopup(true);
            }
        }
    } finally {
        setIsLoading(false);
    }
};


  // Helper function to parse time slots like "7:00 AM" into minutes since midnight
  // Used for comparing session times against current time
  const parseTimeToMinutes = (timeSlot: string): number => {
    const [time, period] = timeSlot.split(" ");
    const [hours, minutes] = time.split(":").map(Number);

    let hour24 = hours;
    if (period === "PM" && hours !== 12) hour24 += 12;
    if (period === "AM" && hours === 12) hour24 = 0;

    return hour24 * 60 + minutes;
  };

  

  // Helper function to get next upcoming session for a class with proper time logic
  const getNextUpcomingSession = (
    googleMeetLinks: Array<{
      id: string;
      url: string;
      title: string;
      time: string;
    }>,
    classDurationMinutes: number
  ):
    | {
      time: string;
      title: string;
      url: string;
      id: string;
    }
    | undefined => {
    if (!googleMeetLinks || googleMeetLinks.length === 0) return undefined;

    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    // Sort sessions by time
    const sortedSessions = googleMeetLinks
      .map((link) => ({
        ...link,
        timeInMinutes: parseTimeToMinutes(link.time),
      }))
      .sort((a, b) => a.timeInMinutes - b.timeInMinutes);

    // Find the next available session
    for (const session of sortedSessions) {
      const sessionEndTime = session.timeInMinutes + classDurationMinutes;
      // If current time is before session end time, this is the active/next session
      if (currentMinutes < sessionEndTime) {
        return session;
      }
    }

    // If all sessions have ended today, return the first session (for tomorrow)
    return sortedSessions[0];
  };

  // Helper function to check if class is currently active (within date range)
  const isClassActive = (startDate: string, endDate: string): boolean => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999); // Include the full end day

    return now >= start && now <= end;
  };

  // Helper function to get booking type counts for a class
  const getBookingTypeCounts = (bookings: BookingData[]) => {
    const counts = { session: 0, monthly: 0, yearly: 0 };
    // Note: You'll need to add a field to track purchase type in your booking data
    // For now, this is a placeholder implementation
    bookings.forEach((booking) => {
      // Assuming you add a purchaseType field to BookingData interface
      // counts[booking.purchaseType || 'session']++;
      counts.session++; // Placeholder - update based on actual data structure
    });
    return counts;
  };

  const handleFetchClassesData = async () => {
    try {
      const res = (await fetchClasses()) as BackendResponse;

      if (res?.data) {
        // Set all bookings for students section
        setAllBookings(res.data.bookings || []);

        if (res.data.classes && Array.isArray(res.data.classes)) {
          const mappedClasses: MappedClass[] = res.data.classes.map(
            (classData: BackendClassData) => {
              const startDateFormatted = toIsoDate(classData.classStartDate);
              const endDateFormatted = toIsoDate(classData.classEndDate);

              // Parse duration to get minutes - handle "45 mins" format
              const durationStr = classData.classDuration.replace(/[^\d]/g, "");
              const durationMinutes = parseInt(durationStr) || 60;

              // Generate schedule string from Google Meet links
              const scheduleString =
                classData.googleMeetLinks &&
                  classData.googleMeetLinks.length > 0
                  ? classData.googleMeetLinks
                    .map((link) => link.time)
                    .join(" | ")
                  : "Schedule not available";

              // Get next upcoming session with duration consideration
              const nextSession = getNextUpcomingSession(
                classData.googleMeetLinks,
                durationMinutes
              );

              // Get active days from schedule
              const activeDays = classData.schedule
                ? Object.entries(classData.schedule)
                  .filter(([day, config]) => config.isActive)
                  .map(([day]) => day)
                : [];

              return {
                id: classData._id,
                title: classData.classTitle,
                type: classData.classCategory,
                description: classData.classDescription,
                duration: classData.classDuration,
                enrolled: classData.bookingCount,
                capacity: 20, // You might want to add this to backend
                startDate: startDateFormatted,
                endDate: endDateFormatted,
                schedule: scheduleString,
                price: parseFloat(classData.classPrice.session) || 0,
                meetLink: classData.googleMeetLinks[0]?.url || "",
                status: isClassActive(
                  classData.classStartDate,
                  classData.classEndDate
                )
                  ? "active"
                  : "inactive",
                level: classData.classCategory || "All Levels",
                googleMeetLinks: classData.googleMeetLinks || [],
                bookings: classData.bookings || [],
                bookingCount: classData.bookingCount,
                nextUpcomingSession: nextSession,
                classDurationMinutes: durationMinutes,
                // Add new fields for comprehensive pricing display
                classPrice: classData.classPrice,
                purchaseStatus: classData.purchaseStatus,
                pricingType: classData.pricingType,
                demoDuration: classData.demoDuration,
                scheduleData: classData.schedule,
                classThumbnail: classData.classThumbnail,
              };
            }
          );

          setClasses(mappedClasses);
        }
      }
    } catch (error) {
      console.error("Error fetching classes data:", error);
      toast.error("Failed to fetch classes data");
    }
  };

  

  useEffect(() => {
    handleFetchProfileData();
    handleFetchClassesData();
  }, []);

  useEffect(() => {
    if (activeTab === "schedule") {
      handleFetchTodaysSchedule();
    }
  }, [activeTab]);

  const handleFetchTodaysSchedule = async () => {
    setLoadingSchedule(true);
    try {
      const data: any = await fetchTodaysSchedule();
      setTodaysScheduleData(data);
    } catch (error) {
      toast.error("Failed to load today's schedule");
    } finally {
      setLoadingSchedule(false);
    }
  };

  // Calculate total students from all bookings
  const totalStudents = allBookings.length;
  const totalClasses = classes.length;
  const averageRating = 4.9; // You might want to calculate this from actual reviews
  
  // Use backend API for total earnings
  const totalEarnings = profileData.totalEarnings;

  // Sample instructor data with dynamic values
  const instructor = {
    name: profileData["firstName"] + " " + profileData["lastName"],
    title: "Certified Yoga Instructor",
    email: profileData["email"],
    phone: profileData["phone"],
    mentorId: profileData["mentorId"] || "",
    experience: profileData["experience"],
    certification: profileData["certification"],
    avatar: profileData["profileIcon"] || "👩‍🏫",
    rating: averageRating,
    totalStudents: totalStudents,
    totalClasses: totalClasses,
    yearlyEarnings: totalEarnings,
    specialties: profileData["skills"] || [],
    socialLinks: profileData["socialLinks"] || {},
    location: profileData["location"] || "",
  };

  const stats = [
    {
      label: "Active Students",
      value: instructor.totalStudents,
      icon: Users,
      color: "blue",
      change: "+12%",
    },
    {
      label: "Classes Taught",
      value: instructor.totalClasses,
      icon: BookOpen,
      color: "green",
      change: "+8%",
    },
    {
      label: "Average Rating",
      value: instructor.rating,
      icon: Star,
      color: "yellow",
      change: "+0.1",
    },
    {
      label: "Yearly Earnings",
      value: `${instructor.yearlyEarnings === 0 ? "-" : "₹" + instructor.yearlyEarnings
        }`,
      icon: IndianRupee,
      color: "purple",
      change: "+15%",
    },
  ];

  const copyMeetLink = (cls: MappedClass) => {
    // Copy the current active session link
    const linkToCopy = cls.nextUpcomingSession?.url || cls.meetLink;
    navigator.clipboard.writeText(linkToCopy);
  };

  const handleStatClick = (label: string) => {
    if (label === "Active Students") {
      handleTabChange("students");
      // Scroll to content area after state update
      setTimeout(() => {
        const contentSection = document.getElementById("tab-content");
        if (contentSection) {
          contentSection.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  };

  const openDeleteModal = (classId: string) => {
    setClassToDelete(classId);
    setShowDeleteModal(true);
  };

  const confirmDeleteClass = async () => {
    if (classToDelete) {
      const res = (await deleteClass(classToDelete)) as deleteApiRes;
      if (res.success) {
        toast.success("Class deleted successfully");
        setClasses((prevClasses) =>
          prevClasses.filter((c) => c.id !== classToDelete)
        );
      } else {
        toast.error("Failed to delete class");
      }
      setShowDeleteModal(false);
      setClassToDelete(null);
    }
  };
  // mandatory fields

  const handleMandatorySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmittingDetails(true);

    const formData = new FormData(e.currentTarget);
    
    // Bank details ko object format mein backend ko bhejne ke liye logic
    const bankDetails = {
        accountHolderName: formData.get('accountHolderName'),
        accountNumber: formData.get('accountNumber'),
        ifscCode: formData.get('ifscCode'),
        bankName: formData.get('bankName'),
    };

    const submitData = new FormData();
    // Certificate file jo input name="certificate" se aayegi
    const certificateFile = (e.currentTarget.elements.namedItem('certificate') as HTMLInputElement).files?.[0];
    
    if (certificateFile) {
        submitData.append("certificate", certificateFile);
    }
    
    submitData.append("mentorId", profileData.mentorId || "");
    submitData.append("bankDetails", JSON.stringify(bankDetails));

    try {
        // Aapka fetch ya axios call
        const response:any = await adminApi.post("/completeMandatoryDetails", submitData,{
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        console.log(response)

        const result = response.data;

        if (response.data.success === true) {
            toast.success("Details updated successfully!");
            setShowMandatoryPopup(false);
            // Profile data ko refresh karein taaki dashboard unlock ho jaye
            handleFetchProfileData(); 
        } else {
            toast.error(result.message || "Update failed");
        }
    } catch (error) {
        console.error("Submission error:", error);
        toast.error("Something went wrong!");
    } finally {
        setSubmittingDetails(false);
    }
};

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading profile...</p>
      </div>
    );
  }

  if (profileData.role && profileData.role !== "Yoga Instructor") {
    return <ComingSoon />;
  }

  return (
    <>
    <AnimatePresence>
  {showMandatoryPopup && (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl overflow-y-auto max-h-[90vh]"
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-teal-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">One Time Verification</h2>
          <p className="text-sm text-gray-500 mt-2">Please complete your payout details to continue using Metafit.</p>
        </div>

       <form onSubmit={handleMandatorySubmit} className="space-y-4">
  <div className="space-y-3">
    {/* 'name' attributes zaroori hain backend map karne ke liye */}
    <input name="accountHolderName" required placeholder="Account Holder Name" className="w-full p-3 border rounded-xl outline-none focus:ring-2 focus:ring-teal-500" />
    <input name="accountNumber" required placeholder="Account Number" className="w-full p-3 border rounded-xl outline-none focus:ring-2 focus:ring-teal-500" />
    <div className="grid grid-cols-2 gap-3">
      <input name="ifscCode" required placeholder="IFSC Code" className="w-full p-3 border rounded-xl outline-none focus:ring-2 focus:ring-teal-500" />
      <input name="bankName" required placeholder="Bank Name" className="w-full p-3 border rounded-xl outline-none focus:ring-2 focus:ring-teal-500" />
    </div>
  </div>

  <div className="p-4 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
      <Upload size={16} /> Professional Certification (PDF/JPG)
    </label>
    {/* Niche 'name' attribute ko "certificate" rakhein kyunki backend upload.single("certificate") expect kar raha hai */}
    <input name="certificate" type="file" required accept=".pdf,image/*" className="text-xs w-full" />
  </div>

  <button 
    disabled={submittingDetails}
    type="submit"
    className="w-full py-4 bg-gradient-to-r from-teal-500 to-purple-600 text-white rounded-xl font-bold shadow-lg hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
  >
    {submittingDetails ? (
        <><Loader2 className="animate-spin" size={18} /> Saving...</>
    ) : (
        "Save & Access Dashboard"
    )}
  </button>
</form>
      </motion.div>
    </div>
  )}
</AnimatePresence>


    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <Helmet>
        <title>{instructor.name} - Yoga Instructor</title>
      </Helmet>
       {showCertificateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-[60] p-4" onClick={() => setShowCertificateModal(false)}>
          <div className="relative max-w-4xl w-full max-h-[90vh] bg-white p-2 rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowCertificateModal(false)} className="absolute -top-3 -right-3 bg-white text-black rounded-full p-1.5 z-10 shadow-lg"><X size={22} /></button>
            <iframe src={certificateUrl} className="w-full h-[85vh] rounded-md border-0" title="Certificate Viewer"></iframe>
          </div>
        </div>
      )}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteClass}
      />
      <SubRoleSelector 
        vendorId={profileData.mentorId || ""}
        vendorRole={profileData.role}
        vendorSubRole={profileData.subRole}
        onSuccess={handleFetchProfileData}
      />
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row items-center lg:items-center justify-between gap-6">
            {/* Profile Info */}
            <div className="flex flex-col sm:flex-row items-center text-center sm:text-left gap-6">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div className="w-24 h-24 sm:w-20 sm:h-20 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-500 rounded-full flex items-center justify-center text-white text-3xl sm:text-2xl font-bold shadow-lg">
                  {profileData.profileIcon ? (
                    <img
                      src={profileData.profileIcon}
                      alt={instructor.name}
                      className="rounded-full h-full w-full object-cover"
                    />
                  ) : (
                    <span>
                      {profileData.firstName?.charAt(0)}
                      {profileData.lastName?.charAt(0)}
                    </span>
                  )}
                </div>
                <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full w-6 h-6 border-2 border-white"></div>
              </div>

              {/* Details */}
              <div>
                <h1 className="text-2xl sm:text-3xl capitalize font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  {instructor.name}
                </h1>
                <p className="text-md sm:text-lg text-gray-600 font-medium">
                  {instructor.title}
                </p>
                <p className="text-gray-500 text-sm sm:text-base flex items-center flex-wrap gap-1">
                  {/* Experience dikhane ke liye */}
                  {instructor.experience && (
                    <span>{instructor.experience} Experience</span>
                  )}
                  {instructor.location && (
                    <>
                      <span className="mx-1 hidden sm:inline">•</span>
                      <span className="flex items-center gap-1">
                        <MapPin size={14} className="text-pink-500" />
                        {typeof instructor.location === 'object' 
                          ? [(instructor.location as any).city, (instructor.location as any).state].filter(Boolean).join(', ')
                          : instructor.location}
                      </span>
                    </>
                  )}

                  {instructor.certification && instructor.certification.length > 0 && (
                    <>
                      {/* <span className="mx-1">•</span> */}
                      <button
                        onClick={() => handleViewCertificate(instructor.certification!)}
                        className="text-teal-600 hover:text-teal-700 font-medium underline decoration-dotted underline-offset-4"
                      >
                        View Certificate
                      </button>
                    </>
                  )}
                </p>
                <div className="flex items-center justify-center sm:justify-start gap-x-4 mt-2">
                  {instructor.socialLinks?.instagram && (
                    <a
                      href={instructor.socialLinks.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-pink-500"
                    >
                      <Instagram size={24} />
                    </a>
                  )}
                  {instructor.socialLinks?.facebook && (
                    <a
                      href={instructor.socialLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-blue-600"
                    >
                      <Facebook size={24} />
                    </a>
                  )}
                  {instructor.socialLinks?.telegram && (
                    <a
                      href={instructor.socialLinks.telegram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-sky-500"
                    >
                      <MessageCircle size={24} />
                    </a>
                  )}
                  {instructor.socialLinks?.youtube && (
                    <a
                      href={instructor.socialLinks.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-red-600"
                    >
                      <Youtube size={24} />
                    </a>
                  )}
                  {instructor.socialLinks?.whatsapp && (
                    <a
                      href={instructor.socialLinks.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-red-600"
                    >
                      <Phone size={24} />
                    </a>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
                  {instructor.specialties.map((specialty, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-4 lg:mt-0">
              <Link
                to={"/instructor/instructor-profile-settings"}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg text-sm sm:text-base"
              >
                <User className="w-5 h-5 mr-2" />
                Profile
              </Link>
              {/* <Link to={"/instructor/instructor-profile-blogs"} className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg text-sm sm:text-base">
                                <BookOpen className="w-5 h-5 mr-2" />
                                Blogs
                            </Link> */}
              <button
                className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg text-sm sm:text-base"
                onClick={() => {
                  if (profileData?.mentorId) {
                    navigate("/instructor/instructor-profile-blogs", {
                      state: { mentorId: profileData.mentorId },
                    });
                  } else {
                    console.warn("Mentor ID not available");
                  }
                }}
              >
                <BookOpen className="w-5 h-5 mr-2" />
                Blogs
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              onClick={() => handleStatClick(stat.label)}
              className={`bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 ${
                stat.label === "Active Students" ? "cursor-pointer hover:scale-105" : ""
              }`}>
              <div className="flex items-center justify-between mb-4">
                <div
                  className={`p-3 rounded-xl ${stat.color === "blue"
                      ? "bg-blue-100 text-blue-600"
                      : stat.color === "green"
                        ? "bg-green-100 text-green-600"
                        : stat.color === "yellow"
                          ? "bg-yellow-100 text-yellow-600"
                          : "bg-purple-100 text-purple-600"
                    }`}
                >
                  <stat.icon className="w-6 h-6" />
                </div>
                <span
                  className={`text-sm font-medium px-2 py-1 rounded-full ${stat.change.startsWith("+")
                      ? "bg-green-100 text-green-600"
                      : "bg-red-100 text-red-600"
                    }`}
                >
                  {stat.change}
                </span>
              </div>
              <div className="space-y-1">
                <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
                <p className="text-gray-600 text-sm">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div id="tab-content" className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 bg-gray-50">
            {[
              { id: "classes", label: "My Classes", icon: BookOpen },
              { id: "schedule", label: "Schedule", icon: Calendar },
              { id: "students", label: "Students", icon: Users },
              { id: "reviews", label: "Reviews", icon: MessageCircle },
              { id: "analytics", label: "Analytics", icon: BarChart3 },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex-1 py-4 px-6 font-medium transition-all ${activeTab === tab.id
                    ? "text-purple-600 border-b-2 border-purple-500 bg-white"
                    : "text-gray-600 hover:text-purple-600 hover:bg-gray-100"
                  }`}
              >
                <tab.icon className="w-5 h-5 inline mr-2" />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* My Classes Tab */}
            {activeTab === "classes" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-800">
                    My Classes
                  </h2>
                  <button
                    onClick={() => setShowAddClass(true)}
                    className="flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add New Class
                  </button>
                </div>

                {/* Add Class Form */}
                <AddClassForm
                  show={showAddClass}
                  onClose={() => setShowAddClass(false)}
                  onCreated={handleFetchClassesData}
                />

                {/* Classes List */}
                {classes.filter((cls) => cls.status === "active").length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {classes
                      .filter((cls) => cls.status === "active")
                      .map((cls) => (
                        <ClassCard
                          key={cls.id}
                          cls={cls}
                          onCopyMeetLink={copyMeetLink}
                          onOpenDeleteModal={openDeleteModal}
                          getBookingTypeCounts={getBookingTypeCounts}
                          onClassUpdated={handleFetchClassesData}
                        />
                      ))}
                  </div>
                ) : (
                  <h1 className="text-xl font-semibold text-gray-400">
                    No Active Classes Are Available.
                  </h1>
                )}
              </div>
            )}

            {/* Updated Schedule Tab */}
            {activeTab === "schedule" && (() => {
              if (loadingSchedule) {
                return (
                  <div className="text-center py-12">
                    <p>Loading schedule...</p>
                  </div>
                );
              }

              if (!todaysScheduleData || !todaysScheduleData.success || !todaysScheduleData.data || !todaysScheduleData.data.classes || todaysScheduleData.data.classes.length === 0) {
                const reason = todaysScheduleData?.reason;
                const details = todaysScheduleData?.details;
                
                let reasonMessage = '';
                let iconColor = 'text-purple-400';
                let bgColor = 'from-purple-50 to-pink-50';
                
                switch(reason) {
                  case 'all_sessions_ended':
                    reasonMessage = `All sessions have ended for today (Current time: ${details?.currentTime})`;
                    iconColor = 'text-orange-400';
                    bgColor = 'from-orange-50 to-amber-50';
                    break;
                  case 'no_classes_on_weekday':
                    reasonMessage = `No classes scheduled for ${details?.today}. You have ${details?.totalActiveClasses} active class(es) but none scheduled for today.`;
                    iconColor = 'text-blue-400';
                    bgColor = 'from-blue-50 to-cyan-50';
                    break;
                  case 'no_classes_in_date_range':
                    reasonMessage = `Today (${details?.today}) is outside any active class date range.`;
                    iconColor = 'text-yellow-400';
                    bgColor = 'from-yellow-50 to-amber-50';
                    break;
                  case 'no_classes_created':
                    reasonMessage = 'You haven\'t created any classes yet.';
                    iconColor = 'text-red-400';
                    bgColor = 'from-red-50 to-pink-50';
                    break;
                  default:
                    reasonMessage = todaysScheduleData?.message || 'No upcoming class scheduled for today';
                }
                
                return (
                  <div className={`bg-gradient-to-br ${bgColor} rounded-2xl p-12 border-2 border-purple-100 text-center`}>
                    <Calendar className={`w-16 h-16 ${iconColor} mx-auto mb-4`} />
                    <p className="text-lg text-gray-700 font-medium">
                      {todaysScheduleData?.message || 'No upcoming class scheduled for today'}
                    </p>
                    {reasonMessage && (
                      <p className="text-sm text-gray-600 mt-2">
                        {reasonMessage}
                      </p>
                    )}
                    {details?.explanation && (
                      <p className="text-xs text-gray-500 mt-3 italic">
                        💡 {details.explanation}
                      </p>
                    )}
                  </div>
                );
              }

              return todaysScheduleData.data.classes.map((backendClass: any, classIdx: number) => (
                <div key={classIdx} className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800">Today's Upcoming Class</h2>
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4 text-white">
                      <h3 className="text-2xl font-bold mt-1">{backendClass.title}</h3>
                      <p className="text-sm font-medium opacity-90">
                        {backendClass.nextSession ? `Next Session: ${backendClass.nextSession.time}` : 'No upcoming session time'}
                      </p>
                    </div>
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600 font-medium">Duration</p>
                          <p className="text-gray-800">{backendClass.duration}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 font-medium">Enrolled Today</p>
                          <p className="text-gray-800">{backendClass.studentCount} students</p>
                        </div>
                        <div>
                          <p className="text-gray-600 font-medium">Type</p>
                          <p className="text-gray-800">{backendClass.type}</p>
                        </div>
                      </div>
                    </div>
                    <div className="px-6 py-4">
                      <h4 className="font-bold text-lg text-gray-800 mb-4">Enrolled Students Today ({backendClass.students ? backendClass.students.length : 0})</h4>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {backendClass.students && backendClass.students.length > 0 ? (
                          backendClass.students.map((student: any, idx: number) => (
                            <div key={student._id || idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition">
                              <div className="flex-1">
                                <p className="font-semibold text-gray-800">{student.studentName || 'Unknown Student'}</p>
                                <p className="text-xs text-gray-600">Contact: {student.phone || 'N/A'}</p>
                              </div>
                              <div className="text-right">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  student.pricingType?.toLowerCase() === 'demo'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : student.pricingType?.toLowerCase() === 'session'
                                    ? 'bg-blue-100 text-blue-800'
                                    : student.pricingType?.toLowerCase() === 'monthly'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-purple-100 text-purple-800'
                                }`}>
                                  {student.pricingType || 'Unknown'}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-500 text-sm">No students scheduled for this specific session.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ));
            })()}

            {/* Students Tab */}
            {activeTab === "students" && (
              <div className="space-y-6">
                {allBookings.length > 0 ? (
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                          <tr>
                            <th className="px-6 py-4 text-left font-semibold text-sm">Student Name</th>
                            <th className="px-6 py-4 text-left font-semibold text-sm">Age</th>
                            <th className="px-6 py-4 text-left font-semibold text-sm">Contact</th>
                            <th className="px-6 py-4 text-left font-semibold text-sm">Class Booked</th>
                            <th className="px-6 py-4 text-left font-semibold text-sm">Pricing Type</th>
                            <th className="px-6 py-4 text-left font-semibold text-sm">Amount Paid</th>
                            <th className="px-6 py-4 text-left font-semibold text-sm">Booking Date</th>
                            <th className="px-6 py-4 text-left font-semibold text-sm">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {allBookings.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((booking, idx) => (
                            <tr key={booking._id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-xs">
                                    {booking.name?.charAt(0).toUpperCase() || 'S'}
                                  </div>
                                  <span className="font-medium text-gray-800">{booking.name || 'Unknown'}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-gray-700">{booking.age || 'N/A'}</td>
                              <td className="px-6 py-4">
                                <div className="flex flex-col gap-1">
                                  <span className="text-sm text-gray-700">{booking.email || 'N/A'}</span>
                                  <span className="text-sm text-gray-600">{booking.phone || 'N/A'}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-gray-800 font-medium">{booking.classTitle || 'N/A'}</span>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                  booking.pricingOption?.toLowerCase() === 'demo'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : booking.pricingOption?.toLowerCase() === 'session'
                                    ? 'bg-blue-100 text-blue-800'
                                    : booking.pricingOption?.toLowerCase() === 'monthly'
                                    ? 'bg-green-100 text-green-800'
                                    : booking.pricingOption?.toLowerCase() === 'yearly'
                                    ? 'bg-purple-100 text-purple-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {booking.pricingOption || 'N/A'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className="font-semibold text-teal-600">₹{booking.totalAmount || 0}</span>
                              </td>
                              <td className="px-6 py-4 text-gray-700">
                                {new Date(booking.bookingDate).toLocaleDateString('en-IN', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </td>
                              <td className="px-6 py-4">
                                {booking.pricingOption?.toLowerCase() === 'demo' ? (
                                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                    booking.demoCompleted 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    {booking.demoCompleted ? 'Completed' : 'Pending'}
                                  </span>
                                ) : (
                                  <div className="flex items-center gap-4 text-sm">
                                    {booking.pricingOption?.toLowerCase() === 'session' && booking.selectedDates?.length ? (
                                      (() => {
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        
                                        const passedSessions = booking.selectedDates.filter((dateStr: string) => {
                                          const sessionDate = new Date(dateStr);
                                          sessionDate.setHours(0, 0, 0, 0);
                                          return sessionDate < today;
                                        }).length;
                                        
                                        const remainingSessions = booking.selectedDates.length - passedSessions;
                                        
                                        // If all sessions have passed, show Completed
                                        if (remainingSessions === 0) {
                                          return (
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                              Completed
                                            </span>
                                          );
                                        }
                                        
                                        return (
                                          <>
                                            <span className="text-gray-700">Attended: <span className="font-semibold text-green-600">{passedSessions}</span></span>
                                            <span className="text-gray-700">Remaining: <span className="font-semibold text-blue-600">{remainingSessions}</span></span>
                                          </>
                                        );
                                      })()
                                    ) : (
                                      <>
                                        <span className="text-gray-700">Attended: <span className="font-semibold text-green-600">{booking.classesAttended || 0}</span></span>
                                        <span className="text-gray-700">Remaining: <span className="font-semibold text-blue-600">{booking.remainingClasses || 0}</span></span>
                                      </>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-600">
                          Total Students: <span className="font-semibold text-gray-800">{allBookings.length}</span>
                        </p>
                        
                        {/* Pagination Controls */}
                        {Math.ceil(allBookings.length / itemsPerPage) > 1 && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                              disabled={currentPage === 1}
                              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                              Previous
                            </button>
                            
                            {/* Page Numbers */}
                            <div className="flex gap-1">
                              {Array.from({ length: Math.ceil(allBookings.length / itemsPerPage) }).map((_, idx) => {
                                const pageNum = idx + 1;
                                const totalPages = Math.ceil(allBookings.length / itemsPerPage);
                                
                                // Show first page, last page, current page, and pages around current
                                if (
                                  pageNum === 1 ||
                                  pageNum === totalPages ||
                                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                ) {
                                  return (
                                    <button
                                      key={pageNum}
                                      onClick={() => setCurrentPage(pageNum)}
                                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                        currentPage === pageNum
                                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                      }`}>
                                      {pageNum}
                                    </button>
                                  );
                                }
                                
                                // Show dots for skipped pages
                                if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                  return (
                                    <span key={`dots-${pageNum}`} className="px-2 py-2 text-gray-500">
                                      ...
                                    </span>
                                  );
                                }
                                
                                return null;
                              })}
                            </div>
                            
                            <button
                              onClick={() => setCurrentPage(prev => Math.min(Math.ceil(allBookings.length / itemsPerPage), prev + 1))}
                              disabled={currentPage === Math.ceil(allBookings.length / itemsPerPage)}
                              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                              Next
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Page Info */}
                      <div className="mt-3 text-xs text-gray-500 text-center">
                        Showing {Math.min((currentPage - 1) * itemsPerPage + 1, allBookings.length)} to {Math.min(currentPage * itemsPerPage, allBookings.length)} of {allBookings.length} students
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
                    <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-500 mb-2">
                      No Students Yet
                    </h3>
                    <p className="text-gray-400">
                      Students will appear here once they book your classes.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Reviews Tab */}
            {activeTab === "reviews" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-800">
                    Student Reviews
                  </h2>
                  <div className="text-right">
                    <div className="flex items-center gap-2 mb-1">
                      <Star
                        className="w-6 h-6 text-yellow-400"
                        fill="currentColor"
                      />
                      <span className="text-2xl font-bold text-gray-800">
                        {instructor.rating}
                      </span>
                      <span className="text-gray-600">/ 5.0</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Based on {allBookings.length} bookings
                    </p>
                  </div>
                </div>

                <div className="text-center py-12">
                  <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-500 mb-2">
                    No Reviews Yet
                  </h3>
                  <p className="text-gray-400">
                    Reviews from your students will appear here.
                  </p>
                  <p className="text-gray-400">Page in under construction!</p>
                </div>
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === "analytics" && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">
                  Analytics Dashboard
                </h2>

                {/* Key Metrics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Total Students */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="text-blue-600 text-sm font-medium mb-1">Total Students</p>
                        <p className="text-3xl font-bold text-blue-900">{allBookings.length}</p>
                      </div>
                      <div className="bg-blue-500 rounded-full p-3">
                        <Users className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <p className="text-blue-600 text-xs">Active learners enrolled</p>
                  </div>

                  {/* Total Revenue */}
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border border-green-200">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="text-green-600 text-sm font-medium mb-1">Total Revenue</p>
                        <p className="text-3xl font-bold text-green-900">
                          ₹{profileData.totalEarnings || 0}
                        </p>
                      </div>
                      <div className="bg-green-500 rounded-full p-3">
                        <IndianRupee className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Classes Organized */}
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="text-purple-600 text-sm font-medium mb-1">Classes Organized</p>
                        <p className="text-3xl font-bold text-purple-900">
                          {classes.length}
                        </p>
                      </div>
                      <div className="bg-purple-500 rounded-full p-3">
                        <Calendar className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <p className="text-purple-600 text-xs">Active classes created</p>
                  </div>

                  {/* Average Rating */}
                  <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-6 border border-yellow-200">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="text-yellow-600 text-sm font-medium mb-1">Average Rating</p>
                        <p className="text-3xl font-bold text-yellow-900">{instructor.rating}</p>
                      </div>
                      <div className="bg-yellow-500 rounded-full p-3">
                        <Star className="w-6 h-6 text-white" fill="currentColor" />
                      </div>
                    </div>
                    <p className="text-yellow-600 text-xs">Out of 5.0 stars</p>
                  </div>
                </div>

                {/* Engagement Stats */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
                  {/* Pricing Distribution */}
                  <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Bookings by Pricing Type</h3>
                    <div className="space-y-4">
                      {(() => {
                        const pricingCount = {
                          demo: allBookings.filter(b => b.pricingOption?.toLowerCase() === 'demo').length,
                          session: allBookings.filter(b => b.pricingOption?.toLowerCase() === 'session').length,
                          monthly: allBookings.filter(b => b.pricingOption?.toLowerCase() === 'monthly').length,
                          yearly: allBookings.filter(b => b.pricingOption?.toLowerCase() === 'yearly').length
                        };
                        return Object.entries(pricingCount).map(([type, count]) => (
                          <div key={type}>
                            <div className="flex justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700 capitalize">{type}</span>
                              <span className="text-sm font-semibold text-gray-900">{count}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${
                                  type === 'demo' ? 'bg-yellow-500' :
                                  type === 'session' ? 'bg-blue-500' :
                                  type === 'monthly' ? 'bg-green-500' : 'bg-purple-500'
                                }`}
                                style={{ width: `${allBookings.length ? (count / allBookings.length) * 100 : 0}%` }}
                              ></div>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* Class Performance */}
                  <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Performing Classes</h3>
                    <div className="space-y-4">
                      {classes.slice(0, 4).map((cls) => {
                        const classBookings = allBookings.filter(b => b.classId === cls.id);
                        const attended = classBookings.reduce((sum, b) => sum + (b.classesAttended || 0), 0);
                        return (
                          <div key={cls.id}>
                            <div className="flex justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700 truncate">{cls.title}</span>
                              <span className="text-sm font-semibold text-gray-900">{classBookings.length} bookings</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
                                style={{ width: `${classes.length ? (classBookings.length / allBookings.length) * 100 : 0}%` }}
                              ></div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{attended} classes conducted</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Student Engagement */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
                  {/* Attendance Stats */}
                  <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Attendance Overview</h3>
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-sm text-gray-600">Avg Attendance</span>
                          <span className="text-sm font-bold text-green-600">
                            {allBookings.length > 0
                              ? Math.round(
                                (allBookings.reduce((sum, b) => sum + (b.classesAttended || 0), 0) /
                                  allBookings.reduce((sum, b) => sum + (b.classesAttended || 0) + (b.remainingClasses || 0), 0)) *
                                100
                              )
                              : 0}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full bg-green-500"
                            style={{
                              width: `${allBookings.length > 0
                                ? (allBookings.reduce((sum, b) => sum + (b.classesAttended || 0), 0) /
                                  allBookings.reduce((sum, b) => sum + (b.classesAttended || 0) + (b.remainingClasses || 0), 0)) *
                                100
                                : 0}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Revenue by Type */}
                  <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Revenue Distribution</h3>
                    <div className="space-y-3">
                      {(() => {
                        const totalRevenue = allBookings.reduce((sum, b) => sum + (b.totalAmount || 0), 0);
                        const demoRevenue = allBookings
                          .filter(b => b.pricingOption?.toLowerCase() === 'demo')
                          .reduce((sum, b) => sum + (b.totalAmount || 0), 0);
                        const sessionRevenue = allBookings
                          .filter(b => b.pricingOption?.toLowerCase() === 'session')
                          .reduce((sum, b) => sum + (b.totalAmount || 0), 0);
                        return (
                          <>
                            <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                              <span className="text-sm text-gray-600">Demo Sessions</span>
                              <span className="font-bold text-gray-900">₹{demoRevenue}</span>
                            </div>
                            <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                              <span className="text-sm text-gray-600">Paid Sessions</span>
                              <span className="font-bold text-gray-900">₹{sessionRevenue}</span>
                            </div>
                            <div className="flex justify-between items-center pt-2">
                              <span className="text-sm font-semibold text-gray-700">Total</span>
                              <span className="font-bold text-green-600">₹{totalRevenue}</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Student Retention */}
                  <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Student Status</h3>
                    <div className="space-y-3">
                      {(() => {
                        const active = allBookings.filter(b => (b.remainingClasses || 0) > 0).length;
                        const completed = allBookings.filter(b => (b.remainingClasses || 0) === 0 && (b.classesAttended || 0) > 0).length;
                        return (
                          <>
                            <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                              <span className="text-sm text-gray-600">Active</span>
                              <span className="font-bold text-blue-600">{active}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Completed</span>
                              <span className="font-bold text-purple-600">{completed}</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Recent Bookings Summary */}
                <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100 mt-8">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Activity</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Student</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Class</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Amount</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allBookings.slice(0, 5).map((booking) => (
                          <tr key={booking._id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-900 font-medium">{booking.name || 'Unknown'}</td>
                            <td className="px-4 py-3 text-gray-700">{booking.classTitle || 'N/A'}</td>
                            <td className="px-4 py-3 font-semibold text-green-600">₹{booking.totalAmount || 0}</td>
                            <td className="px-4 py-3 text-gray-600">
                              {new Date(booking.bookingDate).toLocaleDateString('en-IN')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <InactiveClasses
          inactiveClasses={classes.filter((cls) => cls.status === "inactive")}
        />

        {/* Instructor Tips Section */}
        <div className="mt-8 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-2xl shadow-lg p-8 text-white">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold mb-2">
                Instructor Tips & Resources
              </h3>
              <p className="text-indigo-100">
                Enhance your teaching with these yoga-specific tools and tips
              </p>
            </div>
            <div className="text-6xl opacity-20">🧘‍♀️</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur rounded-xl p-6">
              <div className="flex items-center mb-3">
                <Sun className="w-8 h-8 text-yellow-300 mr-3" />
                <h4 className="font-bold text-lg">Morning Classes</h4>
              </div>
              <p className="text-indigo-100 text-sm">
                Start with gentle warm-ups and breathing exercises. Morning
                students prefer energizing flows to begin their day.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur rounded-xl p-6">
              <div className="flex items-center mb-3">
                <Moon className="w-8 h-8 text-blue-300 mr-3" />
                <h4 className="font-bold text-lg">Evening Classes</h4>
              </div>
              <p className="text-indigo-100 text-sm">
                Focus on relaxation and stress relief. Include restorative poses
                and meditation to help students unwind.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur rounded-xl p-6">
              <div className="flex items-center mb-3">
                <Heart className="w-8 h-8 text-pink-300 mr-3" />
                <h4 className="font-bold text-lg">Student Care</h4>
              </div>
              <p className="text-indigo-100 text-sm">
                Always ask about injuries, offer modifications, and create a
                safe, inclusive environment for all practitioners.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Stats Footer */}
        <div className="mt-8 bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-purple-600 mb-1">
                {allBookings.length > 0
                  ? Math.round(
                    (allBookings.reduce(
                      (sum, booking) => sum + booking.classesAttended,
                      0
                    ) /
                      allBookings.length) *
                    10
                  )
                  : 98}
                %
              </div>
              <div className="text-gray-600 text-sm">Student Satisfaction</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600 mb-1">
                {allBookings.reduce(
                  (sum, booking) => sum + booking.classesAttended,
                  0
                )}
              </div>
              <div className="text-gray-600 text-sm">Classes Conducted</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-1">
                {instructor.totalStudents}
              </div>
              <div className="text-gray-600 text-sm">Active Students</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-600 mb-1">
                {classes.filter((cls) => cls.status === "active").length * 2}
              </div>
              <div className="text-gray-600 text-sm">Hours/Week</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
